import type { PreparedRequest, Transport, TransportResponse } from "@roqeet/core";

/**
 * Transport 구현 — 결정 #7(전송 추상화). Provider가 만든 {@link PreparedRequest}를
 * 실제로 전달한다. Provider와 Transport를 분리했기에 같은 Provider가 direct/
 * proxy/gateway 어디로든 흐를 수 있다.
 */

/** fetch Response를 core의 {@link TransportResponse}로 감싼다. */
function wrap(res: Response): TransportResponse {
  return {
    ok: res.ok,
    status: res.status,
    stream: res.body,
    text: () => res.text(),
  };
}

/**
 * DirectTransport: 브라우저가 프로바이더를 직접 호출(CORS 허용 시).
 * OpenAI/Google/OpenRouter/openai-compatible 등 CORS를 여는 곳에 적합.
 */
export class DirectTransport implements Transport {
  readonly mode = "direct" as const;

  async send(request: PreparedRequest): Promise<TransportResponse> {
    const init: RequestInit = {
      method: request.method,
      headers: request.headers,
    };
    if (request.body !== undefined) init.body = request.body;
    if (request.signal) init.signal = request.signal;
    const res = await fetch(request.url, init);
    return wrap(res);
  }
}

/**
 * ProxyTransport: CORS로 직접 호출이 막힌 프로바이더(Anthropic 등)를 위해
 * 무상태 엣지 패스스루를 경유(결정 #7). 프록시는 키 무저장·무로깅 원칙.
 *
 * 규약: 원래 URL을 `x-roqeet-target` 헤더로 전달하고 프록시 베이스로 POST한다.
 * 실제 프록시(Cloudflare Workers)는 이 헤더를 읽어 그대로 패스스루하도록 구현.
 */
export class ProxyTransport implements Transport {
  readonly mode = "proxy" as const;

  constructor(private readonly proxyBase: string) {}

  async send(request: PreparedRequest): Promise<TransportResponse> {
    const headers: Record<string, string> = {
      ...request.headers,
      "x-roqeet-target": request.url,
      "x-roqeet-method": request.method,
    };
    const init: RequestInit = { method: "POST", headers };
    if (request.body !== undefined) init.body = request.body;
    if (request.signal) init.signal = request.signal;
    const res = await fetch(this.proxyBase, init);
    return wrap(res);
  }
}
